import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { api, authService } from '@soundbyte/services';
import type { User, LoginCredentials, SignupCredentials } from '@soundbyte/types';

// Storage key - can be customized per app via environment variable
// This prevents session conflicts when running multiple apps on localhost
const AUTH_STORAGE_KEY = typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUTH_STORAGE_KEY
  ? import.meta.env.VITE_AUTH_STORAGE_KEY
  : 'soundbyte-auth';

interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Actions
  initialize: () => Promise<void>;
  refreshUser: () => Promise<User | null>;
  checkSession: () => Promise<boolean>;
  login: (credentials: LoginCredentials) => Promise<boolean>;
  signup: (credentials: SignupCredentials) => Promise<boolean>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  updateUser: (updates: Partial<User>) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

// Timestamp of last refreshUser() call — used to prevent stale background checks
// from overwriting freshly restored auth state (e.g., after Stripe payment return)
let lastRefreshTimestamp = 0;

// Format auth errors — surfaces field-level validation details as readable messages
function formatAuthError(error: { message?: string; code?: string; details?: Record<string, unknown> } | null | undefined, fallback: string): string {
  if (!error) return fallback;

  // If validation error with field-level details, format them into a readable message
  if (error.code === 'VALIDATION_ERROR' && error.details?.errors) {
    const fieldErrors = error.details.errors as Record<string, string[]>;
    const messages = Object.values(fieldErrors).flat();
    if (messages.length > 0) {
      return messages.join('. ');
    }
  }

  return error.message || fallback;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,
      error: null,

      // Initialize auth from stored session
      // Trust the persisted user - only clear if backend explicitly says unauthorized
      initialize: async () => {
        const { user: storedUser, isAuthenticated: wasAuthenticated } = get();

        // [Auth] Initialize called, stored user:', storedUser?.email, 'wasAuthenticated:', wasAuthenticated);

        // If we have a valid stored user, trust it immediately (prevents flash to login)
        if (storedUser && storedUser.email && wasAuthenticated) {
          // [Auth] Using stored user, marking as initialized');
          set({ isInitialized: true, isLoading: false });

          // Verify session is still valid with backend (don't block on this)
          authService.initialize().then(user => {
            // If refreshUser was called while this background check was in-flight,
            // don't overwrite the auth state — refreshUser's result is authoritative
            if (Date.now() - lastRefreshTimestamp < 15000) {
              // [Auth] Background session check: skipping — refreshUser was called recently');
              return;
            }

            if (user && user.email) {
              // [Auth] Background session check: valid, updating user data');
              set({ user });
            } else {
              // Session expired — clear state and hard redirect to login
              // [Auth] Background session check: no valid user returned, clearing auth state');
              set({ user: null, isAuthenticated: false });

              // Hard redirect — React Navigate/useEffect may not catch async state changes
              const path = window.location.pathname;
              const search = window.location.search;
              const isAuthPage = path.startsWith('/login') || path.startsWith('/signup') || path.startsWith('/forgot-password') || path.startsWith('/reset-password');
              const isStripeReturn = search.includes('session_id');
              const isSuccessPage = path.includes('/success') || path.includes('/payment-success') || path.includes('/order-success');
              if (!isAuthPage && !isSuccessPage && !isStripeReturn) {
                // [Auth] Hard redirect to login');
                window.location.href = '/login';
              }
            }
          }).catch(() => {
            // Network error - keep stored user
          });

          return;
        }

        // No stored user - check with backend
        set({ isLoading: true });

        try {
          // [Auth] No stored user, checking with backend...');
          const user = await authService.initialize();

          if (user && user.email) {
            // [Auth] Backend returned user:', user.email);
            set({
              user,
              isAuthenticated: true,
              isInitialized: true,
              isLoading: false,
            });
          } else {
            // [Auth] Backend returned no valid user, not authenticated');
            set({
              user: null,
              isAuthenticated: false,
              isInitialized: true,
              isLoading: false,
            });
          }
        } catch (err) {
          // [Auth] Backend check failed:', err);
          // Network error - mark as initialized but not authenticated
          set({
            isInitialized: true,
            isLoading: false,
          });
        }
      },

      // Force refresh user data from server (bypasses cache)
      // Sets a timestamp to prevent stale background checks from overwriting auth
      refreshUser: async () => {
        // [Auth] Refresh user called - fetching fresh data from server');
        lastRefreshTimestamp = Date.now();
        try {
          const user = await authService.initialize();
          if (user && user.email) {
            // [Auth] Refresh successful, tier:', user.subscriptionTier);
            set({ user, isAuthenticated: true });
            return user;
          } else {
            // [Auth] Refresh returned no user');
            return null;
          }
        } catch (err) {
          // [Auth] Refresh failed:', err);
          return null;
        }
      },

      // Check if session is still valid (used by AdminLayout for periodic checks)
      checkSession: async () => {
        // [Auth] Check session called');
        try {
          const result = await authService.initialize();

          if (result && result.email) {
            // [Auth] Session valid for:', result.email);
            set({ user: result, isAuthenticated: true });
            return true;
          }

          // null means no valid user — session expired or no data
          // If we have a stored user, keep current state (could be 304/empty response)
          // If no stored user, mark as not authenticated
          const { user: currentUser } = get();
          if (currentUser && currentUser.email) {
            // [Auth] No fresh user data but have stored user, keeping state');
            return true;
          }

          // [Auth] Session invalid - logging out');
          set({ user: null, isAuthenticated: false });
          return false;
        } catch (err) {
          // [Auth] Session check failed:', err);
          // Network error - don't logout, keep current state
          return get().isAuthenticated;
        }
      },

      // Login
      login: async (credentials) => {
        // [Auth] Login attempt for:', credentials.email);
        set({ isLoading: true, error: null });

        const response = await authService.login(credentials);

        if (response.success && response.data) {
          // [Auth] Login successful:', response.data.user.email);
          set({
            user: response.data.user,
            isAuthenticated: true,
            isInitialized: true,
            isLoading: false,
            error: null,
          });
          return true;
        }

        // [Auth] Login failed:', response.error?.message);
        set({
          isLoading: false,
          error: formatAuthError(response.error, 'Login failed'),
        });
        return false;
      },

      // Signup
      signup: async (credentials) => {
        set({ isLoading: true, error: null });

        const response = await authService.signup(credentials);

        if (response.success && response.data) {
          set({
            user: response.data.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
          return true;
        }

        set({
          isLoading: false,
          error: formatAuthError(response.error, 'Signup failed'),
        });
        return false;
      },

      // Logout
      logout: async () => {
        // [Auth] Logout called');
        set({ isLoading: true });

        try {
          await authService.logout();
        } catch (err) {
          // [Auth] Logout API call failed, but clearing local state anyway:', err);
        }

        // [Auth] Clearing local auth state');
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      },

      // Set user directly (for Google OAuth etc.)
      setUser: (user) => set({ user, isInitialized: true }),

      // Set authentication state directly
      setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated, isInitialized: true }),

      // Update user data locally
      updateUser: (updates) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...updates } });
        }
      },

      // Set error
      setError: (error) => set({ error }),

      // Clear error
      clearError: () => set({ error: null }),
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist user data, not loading/error states
        // Ensure we never persist invalid state (user without email)
        user: state.user?.email ? state.user : null,
        isAuthenticated: state.user?.email ? state.isAuthenticated : false,
      }),
      onRehydrateStorage: () => (state, error) => {
        // [Auth] Rehydrated from storage:', state?.user?.email, 'isAuthenticated:', state?.isAuthenticated);
        if (error) {
          // [Auth] Rehydration error, clearing state');
          useAuthStore.setState({ user: null, isAuthenticated: false });
          return;
        }
        // Fix inconsistent state: isAuthenticated but no valid user
        if (state?.isAuthenticated && (!state.user || !state.user.email)) {
          // [Auth] Inconsistent state: isAuthenticated but no valid user, clearing');
          useAuthStore.setState({ user: null, isAuthenticated: false });
        }
      },
    }
  )
);

// Clear auth state when any API call returns 401 (session expired)
api.setOnUnauthorized(() => {
  // If refreshUser was called recently (e.g., after Stripe payment verification),
  // ignore stale 401s from requests that were sent before the session was restored
  if (Date.now() - lastRefreshTimestamp < 15000) {
    // [Auth] 401 received but refreshUser was called recently — ignoring stale 401');
    return;
  }

  const { isAuthenticated } = useAuthStore.getState();
  if (isAuthenticated) {
    // [Auth] 401 received — session expired, clearing auth state');

    // Clear session token (Safari/iOS fallback)
    api.clearSessionToken();

    useAuthStore.setState({ user: null, isAuthenticated: false });

    // Hard redirect — React Navigate/useEffect may not catch async state changes
    const path = window.location.pathname;
    const isAuthPage = path.startsWith('/login') || path.startsWith('/signup') || path.startsWith('/forgot-password') || path.startsWith('/reset-password');
    const isSuccessPage = path.includes('/success') || path.includes('/payment-success') || path.includes('/order-success');
    if (!isAuthPage && !isSuccessPage) {
      // [Auth] Hard redirect to login');
      window.location.href = '/login';
    }
  }
});

// Selector hooks for common use cases
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useIsAdmin = () => useAuthStore((state) =>
  state.user?.role === 'admin' || state.user?.role === 'superadmin'
);
